import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { HighchartsReactRefObject } from 'highcharts-react-official';
import { ModalTransition } from '@atlaskit/modal-dialog';

import { ChartContent } from 'components/Chart';
import Alert from 'kingpin/Alert';
import Button from 'kingpin/atoms/Button';
import usePopup from 'hooks/usePopup';
import { InsightsChatContext } from 'contextProviders/InsightsChatProvider';
import {
  InsightsChartSpec,
  InsightsChartType,
} from 'services/streamInsightsQuery';

import buildChartOptions, {
  formatChartValue,
  INSIGHTS_CHART_HEIGHT,
} from './buildChartOptions';
import { getCompatibleChartTypes } from './chartOptions/chartTypeMeta';
import ChartTypeSelect from './ChartTypeSelect';
import InsightsMessageGrid from './InsightsMessageGrid';
import SaveInsightsChartModal from './saveChart/SaveInsightsChartModal';
import { canSaveSpec } from './saveChart/buildChartDefFromSpec';
import ExploreChartModal from './explore/ExploreChartModal';

// Temporarily hidden while the save-to-gadget flow is held back. Flip to true to
// bring the "Save chart" affordance back (all its wiring is kept intact below).
const SHOW_SAVE_CHART = false;

const Wrapper = styled.div`
  margin-top: 12px;
`;

const ExcludedOutliersWrapper = styled.div`
  margin-bottom: 8px;
`;

const OutlierList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: var(--color-text-primary);
`;

const ChartBox = styled.div`
  width: 100%;
  height: ${INSIGHTS_CHART_HEIGHT}px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

// Renders one agent-produced chart inline in the chat, plus a "Save chart"
// affordance that persists it as a live gadget on a dashboard/report. The spec
// is fully resolved server-side, so this just maps it to Highcharts options and
// hands it to the lightweight ChartContent renderer.
const InsightsMessageChart = ({ spec }: { spec: InsightsChartSpec }) => {
  const chartRef = useRef<HighchartsReactRefObject | null>(null);
  const { isOpen, open, close } = usePopup();
  const {
    isOpen: isExploreOpen,
    open: openExplore,
    close: closeExplore,
  } = usePopup();
  const { setIsExploreOpen } = useContext(InsightsChatContext);
  // View mode (chart vs data grid) and the user's chosen chart type are local UI
  // state — the spec stays the source of truth for the data.
  const [viewMode, setViewMode] = useState<'chart' | 'grid'>('chart');
  const [chartType, setChartType] = useState<InsightsChartType>(spec.chartType);
  // The spec with the user's chosen type applied. Used for rendering AND carried
  // into Save/Explore so those act on what the user is actually looking at.
  const effectiveSpec = useMemo(
    (): InsightsChartSpec => ({ ...spec, chartType }),
    [spec, chartType],
  );
  // Which types this spec's data can actually render — a function of the data
  // shape, not the current chartType, so it stays stable as the user switches.
  const compatibleTypes = useMemo(() => getCompatibleChartTypes(spec), [spec]);
  const options = buildChartOptions(effectiveSpec);
  const hasData = spec.series.some((s) => s.data.some((v) => v != null));
  // A spec can render inline but still be unable to become a live gadget (e.g.
  // a cross-tab whose second dimension isn't categorical). Disable the buttons
  // with the reason rather than writing (or exploring) a gadget that would
  // render blank — Explore renders the very artefact Save writes, so the same
  // guard covers both.
  const saveable = canSaveSpec(effectiveSpec);

  // The chat suppresses Highcharts' body-level tooltip container while it's
  // open, to stop background dashboard tooltips bleeding over the slideout. The
  // live Explore gadget uses those very tooltips, so tell the chat to lift the
  // suppression while the modal is up.
  const onExploreOpen = useCallback(() => {
    setIsExploreOpen(true);
    openExplore();
  }, [setIsExploreOpen, openExplore]);

  const onExploreClose = useCallback(() => {
    setIsExploreOpen(false);
    closeExplore();
  }, [setIsExploreOpen, closeExplore]);

  return (
    <Wrapper data-testid="insights-message-chart">
      {!!spec.excludedOutliers?.length && (
        <ExcludedOutliersWrapper data-testid="insights-excluded-outliers">
          <Alert
            type="Warning"
            title="Outliers excluded from this chart"
            body={
              <OutlierList>
                {spec.excludedOutliers.map((outlier) => (
                  <li key={outlier.label}>
                    {outlier.value == null
                      ? outlier.label
                      : `${outlier.label} — ${formatChartValue(
                          outlier.value,
                          spec.formatting,
                        )}`}
                  </li>
                ))}
              </OutlierList>
            }
          />
        </ExcludedOutliersWrapper>
      )}
      <ChartBox>
        {viewMode === 'grid' && hasData ? (
          <InsightsMessageGrid spec={effectiveSpec} />
        ) : (
          <ChartContent
            chartRef={chartRef}
            options={options}
            noDataToDisplay={!hasData}
            useSmallNoDataMessage
          />
        )}
      </ChartBox>
      {hasData && (
        <Toolbar>
          {viewMode === 'chart' && compatibleTypes.length > 1 && (
            <ChartTypeSelect
              spec={spec}
              chartType={chartType}
              compatibleTypes={compatibleTypes}
              onSelect={setChartType}
            />
          )}
          <ToolbarRight>
            <Button
              size="Small"
              type={viewMode === 'chart' ? 'Selected' : 'Ghost'}
              icon="chart-outlined"
              onClick={() => setViewMode('chart')}
              testId="insights-chart-view-toggle-chart"
            />
            <Button
              size="Small"
              type={viewMode === 'grid' ? 'Selected' : 'Ghost'}
              icon="table"
              onClick={() => setViewMode('grid')}
              testId="insights-chart-view-toggle-grid"
            />
            <Button
              size="Small"
              type="Ghost"
              label="Explore"
              onClick={onExploreOpen}
              isDisabled={!saveable.ok}
              isDisabledMessage={saveable.ok ? undefined : saveable.reason}
              testId="explore-insights-chart"
            />
            {SHOW_SAVE_CHART && (
              <Button
                size="Small"
                type="Ghost"
                label="Save chart"
                onClick={open}
                isDisabled={!saveable.ok}
                isDisabledMessage={saveable.ok ? undefined : saveable.reason}
                testId="save-insights-chart"
              />
            )}
          </ToolbarRight>
        </Toolbar>
      )}
      <ModalTransition>
        {isOpen && (
          <SaveInsightsChartModal spec={effectiveSpec} onClose={close} />
        )}
      </ModalTransition>
      <ModalTransition>
        {isExploreOpen && (
          <ExploreChartModal spec={effectiveSpec} onClose={onExploreClose} />
        )}
      </ModalTransition>
    </Wrapper>
  );
};

export default InsightsMessageChart;
